import {
  boolean,
  number,
  route,
  string,
  type
} from "seneca-common/utils/routing/react-router-typesafe-routes/dom";

import { TeacherPlatformGatingEventRoutingType } from "features/teachers/features/gating/types";

import {
  anyValidator,
  assignmentsTableModeType,
  courseAddedLocationType,
  createAssignmentSourceType,
  linkPropsType,
  locationType,
  parentPlatformPreviewSourceType,
  preselectClassesOptionType,
  schoolQuoteFormTypeType,
  schoolQuoteSourcesType,
  selectedQuizzesType,
  subscriptionJourneySourcesType,
  toValidator
} from "./types";

export const ASSIGNMENT_LIST_ROUTES = {
  TODO: route("todo"),
  DONE: route("done"),
  MISSED: route("missed")
};

export const TEACHER_ASSIGNMENT_ROUTES = {
  CLASSES: route("classes"),
  STUDENTS: route("students"),
  EDIT: route("edit")
};

export const ROUTES = {
  PARENT: route(
    "parent",
    {},
    {
      CHILD: route(
        "child/:childId",
        { params: { childId: string().default("") } },
        {
          ASSIGNMENTS: route("assignments", {}, ASSIGNMENT_LIST_ROUTES),
          REPORTS: route(
            "report",
            {},
            {
              WEEKLY: route("weekly", {
                searchParams: {
                  week: number(),
                  year: number()
                }
              }),
              MONTHLY: route("monthly", {
                searchParams: {
                  month: number(),
                  year: number()
                }
              })
            }
          ),
          TEACHER_REPORT: route(
            "teacher-report-cards",
            {},
            {
              CARDS: route("overview", {
                state: {
                  isInternal: boolean()
                }
              }),
              CARD: route("card/:reportId/:createdAt/overview", {
                params: {
                  reportId: string().default(""),
                  createdAt: string().default("")
                },
                state: {
                  isInternal: boolean(),
                  createdAt: string().default("")
                }
              })
            }
          )
        }
      )
    }
  ),
  DASHBOARD: route(
    "dashboard",
    {},
    {
      CLASSES: route("classes"),
      ASSIGNMENTS: route(
        "assignments",
        {},
        {
          ...ASSIGNMENT_LIST_ROUTES,
          ASSIGNMENT: route("assignment/:assignmentId", {
            params: { assignmentId: string().default("") }
          })
        }
      ),
      CLASS: route(
        "class/:classId",
        { params: { classId: string().default("") } },
        {
          ASSIGNMENTS: route("assignments", {}, ASSIGNMENT_LIST_ROUTES),
          LEADERBOARDS: route("leaderboards"),
          SETTINGS: route("settings")
        }
      )
    }
  ),
  TEACHER: route(
    "teacher",
    { state: { teacherBackground: locationType } },
    {
      HOME: route("home"),
      SETTINGS: route(
        "settings",
        {},
        {
          PERSONAL_INFORMATION: route("personal-information"),
          PASSWORD: route("password"),
          NOTIFICATIONS: route("notifications")
        }
      ),
      CLASS: route(
        "class/:classId",
        {
          params: { classId: string().default("") }
        },
        {
          ADD_COURSES: route("add-courses"),
          ASSIGNMENTS: route(
            "assignments",
            {},
            {
              OVERVIEW: route("overview"),
              ASSIGNMENT: route(
                "assignment/:assignmentId",
                {
                  params: { assignmentId: string().default("") }
                },
                {
                  REASSIGN: route(
                    "reassign",
                    {},
                    {
                      SECTION: route("section"),
                      DUE: route("due")
                    }
                  )
                }
              )
            }
          ),
          COURSES: route("courses"),
          EDIT_ASSIGNMENT: route(
            "edit-assignment/:assignmentId",
            {
              state: {
                fromPreviousLink: boolean(),
                selectedClassIdsToInvite: string().defined().array(),
                navigateProps: linkPropsType
              }
            },
            {
              TOPICS: route("topics"),
              EXAM_QUESTIONS: route("exam-questions"),
              DETAILS: route("details"),
              INVITE_PARENTS: route("invite-parents")
            }
          ),
          GRADES: route(
            "grades",
            {},
            {
              GRADEBOOK: route("gradebook"),
              INSIGHTS_TOPIC: route("topic"),
              INSIGHTS_COURSE: route("course")
            }
          ),
          INVITE_STUDENTS: route("invite-students"),
          OVERVIEW: route("overview"),
          SETTINGS: route("settings"),
          STUDENTS: route("students"),
          STUDENT: route(
            "student/:studentId",
            {
              params: {
                studentId: string().default("")
              }
            },

            {
              REPORT_CARD: route(
                "report-card",
                {},
                {
                  PREVIEW: route("preview", {
                    state: {
                      isInternal: boolean()
                    }
                  }),
                  SENDING: route("sending", {
                    state: {
                      isDirectlyAccessible: boolean()
                    }
                  }),
                  SUCCESS: route("success", {
                    state: {
                      isDirectlyAccessible: boolean()
                    }
                  })
                }
              )
            }
          ),
          KNOWLEDGE_GAPS: route(
            "knowledge-gaps",
            {
              state: { formData: type(anyValidator) }
            },
            {
              SECTIONS: route("sections"),
              DETAILS: route("details")
            }
          )
        }
      ),
      CLASSES: route("classes", {
        searchParams: { preselectClasses: preselectClassesOptionType },
        state: { deselectClasses: boolean() }
      }),
      ASSIGNMENTS: route(
        "assignments",
        {},
        {
          CREATE: route(
            "create",
            {
              state: {
                withClassSelection: boolean(),
                source: createAssignmentSourceType,
                classId: string().default(""),
                selectedClassIdsToInvite: string().default("").array(),
                fromPreviousLink: boolean(),
                selectedQuizzes: selectedQuizzesType
              }
            },
            {
              CLASS: route("class", {
                state: { clearSelectedClasses: boolean() }
              }),
              TYPE: route("type"),
              TOPICS: route("topics"),
              QUIZZES: route("quizzes"),
              EXAM_QUESTIONS: route("exam-questions"),
              DETAILS: route("details"),
              INVITE_PARENTS: route("invite-parents")
            }
          ),
          ASSIGNMENT: route(
            ":assignmentId",
            {
              params: { assignmentId: string().default("") },
              state: {
                previousPathname: string().default("")
              }
            },
            TEACHER_ASSIGNMENT_ROUTES
          )
        }
      ),
      REPORTS: route(
        "reports",
        {},
        {
          STUDENTS: route("students"),
          TEACHERS: route("teachers"),
          PARENTS: route("parents"),
          CUSTOM: route("custom")
        }
      ),
      SCHOOL: route(
        "school",
        {},
        {
          CLASSES: route("classes"),
          STUDENTS: route("students"),
          TEACHERS: route("teachers")
        }
      ),
      QUIZZES: route(
        "quizzes",
        {},
        {
          QUIZ: route(
            ":quizId",
            {
              params: { quizId: string().default("") }
            },
            {
              DELETE_MODAL: route("delete"),
              SAVE_MODAL: route("save", {
                state: {
                  lastLocation: locationType,
                  quizId: string().default(""),
                  isPublished: boolean().default(false)
                }
              })
            }
          ),
          CREATE: route(
            "create",
            { state: { lastLocation: locationType } },
            {
              DETAILS: route("details"),
              INPUT: route("input"),
              GENERATING: route("generating")
            }
          ),
          SCHOOL: route("school")
        }
      ),
      KNOWLEDGE_GAPS: route(
        "knowledge-gaps",
        {
          state: { classId: string().default(""), formData: type(anyValidator) }
        },
        {
          SECTIONS: route("sections"),
          DETAILS: route("details")
        }
      ),
      WHAT_IS_PREMIUM: route(
        "what-is-premium",
        {
          state: { source: subscriptionJourneySourcesType }
        },
        {
          GET_A_QUOTE: route(
            "get-a-quote",
            {
              searchParams: {
                formType: schoolQuoteFormTypeType,
                source: schoolQuoteSourcesType
              },
              state: {
                isInternal: boolean()
              }
            },
            {
              SUCCESS: route("success")
            }
          )
        }
      ),
      RESOURCES: route("resources"),
      BECOME_A_TEACHER: route("become-a-teacher", {
        state: { referrer: locationType }
      })
    }
  ),
  CLASSROOM: route(
    "classroom",
    {},
    {
      COURSE: route(
        "course/:courseId",
        {
          params: { courseId: string().default("") }
        },
        {
          SECTION: route(
            "section/:sectionId",
            {
              params: { sectionId: string().default("") },
              searchParams: {
                mode: string().default("")
              }
            },
            {
              SESSION: route("session"),
              CONTENT: route(
                "content/:contentId",
                {
                  params: { contentId: string().default("") }
                },
                {
                  MODULE: route("module/:moduleId", {
                    params: {
                      moduleId: string().default("")
                    }
                  })
                }
              ),
              HOMEPAGE: route("section-overview"),
              EXAM_QUESTIONS: route(
                "exam-questions",
                {},
                {
                  SESSION: route("session")
                }
              )
            }
          ),
          ASSIGNMENTS: route("assignments", {}, ASSIGNMENT_LIST_ROUTES)
        }
      ),
      QUIZ_SESSION: route("quiz-session/:quizId", {
        params: { quizId: string().default("") },
        state: {
          locationFrom: string().default(""),
          assignmentId: string().default(""),
          completesAssignment: boolean().default(false),
          reviewingSession: boolean().default(false)
        }
      })
    }
  ),
  DIAGNOSIS: route("diagnosis", {
    state: {
      noUpdateRedirect: boolean()
    }
  }),
  UPDATE: route("update", {
    state: {
      redirectPostUpdate: type(toValidator),
      redirectState: type(anyValidator)
    }
  })
};

export const MODAL_ROUTES = route(
  "",
  { state: { background: locationType } },
  {
    MIS_CLASS_TRIGGER: route("sync-information"),
    INVITE_PARENT: route("invite-parent"),
    SHARE_ASSIGNMENT: route("share-assignment", {
      searchParams: {
        classId: string(),
        assignmentId: string().default(""),
        courseId: string()
      },
      state: {
        courseName: string().default(""),
        dueDate: string()
      }
    }),
    MANAGE_STUDENT: route("manage-student", {
      searchParams: {
        classId: string().default(""),
        userId: string().default("")
      },
      state: { source: string().default("") }
    }),
    ASSIGNMENT_STUDENT_RESULT: route("student-assignment-result", {
      searchParams: {
        classId: string().default(""),
        assignmentId: string().default(""),
        userId: string().default("")
      },
      state: {
        orderedAssigneeIds: string().default("").array(),
        defaultTableMode: assignmentsTableModeType
      }
    }),
    DELETE_CLASS_ASSIGNMENT: route("delete-class-assignment", {
      searchParams: {
        classId: string().default(""),
        assignmentId: string().default("")
      }
    }),
    DELETE_CLASS_ASSIGNMENTS: route("delete-class-assignments", {
      searchParams: {
        assignmentId: string().default(""),
        classId: string()
      },
      state: {
        className: string()
      }
    }),
    EDIT_MULTI_CLASS_ASSIGNMENT: route("edit-multi-class-assignment", {
      searchParams: {
        classId: string().default(""),
        assignmentId: string().default("")
      }
    }),
    START_SPEEDY_MARKING: route("start-speedy-marking", {
      searchParams: {
        classId: string().default(""),
        assignmentId: string().default("")
      },
      state: {
        selectedQuestionIds: string().default("").array()
      }
    }),
    MARKING: route("marking", {
      searchParams: {
        classId: string().default(""),
        assignmentId: string().default(""),
        userId: string().default(""),
        questionId: string().default("")
      },
      state: {
        orderedAssigneeIds: string().default("").array()
      }
    }),
    INSIGHTS_SECTIONS_SELECTOR: route("grades-section-selector", {
      searchParams: { classId: string().default("") }
    }),
    REMOVE_COURSE: route("remove-course", {
      searchParams: {
        classId: string().default(""),
        courseId: string().default("")
      }
    }),
    CHOOSE_REGION: route("choose-region"),
    INVITE_PARENT_INFO: route("invite-parent-info", {
      state: {
        classIds: string().defined().array(),
        analyticsSource: schoolQuoteSourcesType
      }
    }),
    INVITE_CLASSES_PARENTS: route("classes-invite-parents", {
      state: {
        classIds: string().defined().array(),
        ineligibleClassIds: string().defined().array(),
        analyticsSource: schoolQuoteSourcesType
      }
    }),
    CHANGE_EMAIL: route("change-email"),
    DELETE_CLASS: route("delete-class", {
      searchParams: { classId: string().default("") }
    }),
    LEAVE_CLASS: route("leave-class", {
      searchParams: { classId: string().default("") }
    }),
    ARCHIVE_CLASSES: route("archive-classes", {
      state: { classIds: string().default("").array() }
    }),
    HIDE_CLASSES: route("hide-classes", {
      state: { classIds: string().default("").array() }
    }),
    UNHIDE_CLASSES: route("unhide-classes", {
      state: { classIds: string().default("").array() }
    }),
    PROGRESS_REPORT: route("teacher-custom-report", {
      searchParams: {
        requestId: string().default("")
      }
    }),
    JOIN_CLASS_STUDENT: route("join-class-student"),
    INVITE_TEACHER_STUDENT: route("invite-teacher-student"),
    CLASS_DOES_NOT_EXIST: route("class-does-not-exist"),
    MANAGE_TEACHERS: route("manage-teachers", {
      searchParams: { classId: string().default("") }
    }),
    TRANSFER_OWNERSHIP: route("transfer-ownership", {
      searchParams: {
        classId: string().default(""),
        userId: string().default("")
      }
    }),
    REMOVE_TEACHER: route("remove-teacher", {
      searchParams: {
        classId: string().default(""),
        userId: string().default("")
      }
    }),
    RESET_PASSWORDS_SCHOOL: route("reset-passwords-school"),
    JOIN_SCHOOL_CLASS: route("join-school-class", {
      state: {
        classId: string().default(""),
        schoolId: string().default("")
      }
    }),
    JOIN_SCHOOL_CLASSES: route("join-school-classes", {
      state: {
        classIds: string().default("").array(),
        schoolId: string().default("")
      }
    }),
    CLASS_STUDENT_INVITE_SUCCESS: route("class-student-invite-success", {
      state: { isDirectlyAccessible: boolean() }
    }),
    SCHOOL_RENEWAL_FORM_SUCCESS: route("school-renewal-form-success", {
      state: { contactEmail: string().default("") }
    }),
    MIS_EXPIRY: route("mis-expiry", {
      state: {
        gatingEvent: TeacherPlatformGatingEventRoutingType,
        redirectTo: locationType
      }
    }),
    REVIEW_MCA_MARKING: route("review-mca-marking", {
      state: {
        classId: string().default(""),
        assignmentId: string().default("")
      }
    }),
    ENROLL: route("start/:courseId", {
      params: { courseId: string().default("") }
    }),
    SHARE_SECTION: route("share-section", {
      state: {
        courseId: string().default(""),
        sectionId: string().default("")
      }
    }),
    GET_QUIZ_MODE: route("get-quiz-mode", {
      state: {
        courseId: string().default(""),
        sectionId: string().default("")
      }
    }),
    GET_WRONG_ANSWERS_MODE: route(
      "get-wrong-answers",
      {
        state: {
          courseId: string().default(""),
          sectionId: string().default(""),
          wrongAnswersCount: number()
        }
      },
      {
        TRIAL: route("trial")
      }
    ),
    DOWNLOAD_NOTES: route("download-notes", {
      searchParams: {
        courseId: string().default(""),
        sectionId: string().default("")
      }
    }),
    QUIZ_UPDATED: route("quiz-updated", {
      state: {
        quizId: string().default(""),
        isPublished: boolean().default(false)
      }
    }),
    QUIZ_DELETED: route("deleted"),
    QUIZ_PREMIUM_TRIGGER: route("premium", {
      state: {
        analyticsSource: schoolQuoteSourcesType
      }
    }),
    QUIZ_SHARE_SYNCED_GATING: route("quiz-share-synced", {
      state: {
        analyticsSource: schoolQuoteSourcesType
      }
    }),
    SECTION_PREVIEW: route("section-preview", {
      state: {
        courseId: string().default(""),
        sectionId: string().default(""),
        message: string(),
        alert: string()
      }
    }),
    PREMIUM_COURSES: route("premium-courses", {}),
    ADD_COURSE_TO_CLASS: route("add-course-to-class", {
      searchParams: {
        classId: string().default(""),
        courseId: string().default("")
      },
      state: {
        source: courseAddedLocationType
      }
    }),
    CHILD_SWITCHER: route("child-switcher", {}),
    PARENT_INVITE_CHILDREN: route("invite-children", {}),
    INVITE_TO_CLASS: route("invite-to-class", {
      searchParams: { classId: string().default("") }
    }),
    UNARCHIVE_CLASSES: route("unarchive-classes", {
      state: { classIds: string().default("").array() }
    }),
    CREATE_CLASS: route("create-class"),
    INVITE_STUDENTS_QR_CODE: route("invite-students-qr-code", {
      searchParams: {
        classId: string().default("")
      },
      state: {
        lastLocation: locationType
      }
    }),
    END_QUIZ_SESSION: route(
      "end-quiz-session/:quizId",
      {
        params: { quizId: string().default("") },
        state: {
          quizSessionData: type(anyValidator),
          assignmentId: string().default(""),
          completesAssignment: boolean().default(false)
        }
      },
      {
        SUMMARY: route("summary-overview"),
        ASSIGNMENT_COMPLETE: route("assignment-complete")
      }
    ),
    QUIT_QUIZ_SESSION: route("quit-quiz-session", {
      state: {
        quitSessionPath: string().default("")
      }
    }),
    PARENT_PLATFORM_PREVIEW: route("parent-platform-preview", {
      state: {
        source: parentPlatformPreviewSourceType
      }
    }),
    GATE_AMELIA_SECTION: route("premium-amelia-section"),
    QUIZ_PREVIEW_MODAL: route("quiz-preview", {
      state: {
        quizId: string().default("")
      }
    }),
    ASSIGN_UNPUBLISHED_QUIZ_MODAL: route("assign-unpublished-quiz", {
      state: {
        quizId: string().default("")
      }
    }),
    PARENT_MARKETING: route("parent-marketing-preferences"),
    CLAIM_TUTORING_TRIAL: route("claim-tutoring-trial"),
    COMPLETE_TEACHER_ACCOUNT: route("complete-teacher-account")
  }
);
